// components/PurchaseOrderReportPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import reportsAPI from '../api/reportsAPI';
import { formatDate } from '../utils/dateUtils';
import { formatNumber } from '../utils/numberUtils';

const PurchaseOrderReportPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    
    // Filter states
    const [dateRange, setDateRange] = useState({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [filters, setFilters] = useState({
        status: '',
        supplierId: '',
        storeId: ''
    });

    const format = num => num?.toLocaleString('en-US') || '0';

    const fetchPurchaseOrderReport = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const params = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                ...filters
            };
            
            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });
            
            const response = await reportsAPI.getPurchaseOrderReport(params);
            setReportData(response.data);
        } catch (err) {
            console.error('Error fetching purchase order report:', err);
            setError(err.message || 'Failed to load purchase order report');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchaseOrderReport();
    }, []);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDateRangeChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleGenerateReport = () => {
        fetchPurchaseOrderReport();
    };

    const handlePrintReport = () => {
        try {
            console.log('Preparing purchase order report for printing...');
            
            if (!reportData || !reportData.purchaseOrders || reportData.purchaseOrders.length === 0) {
                alert('No purchase order data available to print');
                return;
            }
    
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                alert('Please allow popups for printing');
                return;
            }
    
            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Purchase Order Report - ${new Date().toLocaleDateString()}</title>
                    <style>
                        @media print {
                            @page {
                                margin: 0.5in;
                                size: letter;
                            }
                            body {
                                font-family: Arial, sans-serif;
                                font-size: 12px;
                                line-height: 1.4;
                                color: #000;
                                margin: 0;
                                padding: 0;
                            }
                            .print-header {
                                text-align: center;
                                border-bottom: 2px solid #000;
                                padding-bottom: 10px;
                                margin-bottom: 20px;
                            }
                            .print-header h1 {
                                margin: 0;
                                font-size: 18px;
                                font-weight: bold;
                            }
                            .print-header .subtitle {
                                margin: 5px 0 0 0;
                                font-size: 14px;
                                color: #666;
                            }
                            .print-summary {
                                margin-bottom: 20px;
                            }
                            .print-table {
                                width: 100%;
                                border-collapse: collapse;
                                margin-bottom: 20px;
                            }
                            .print-table th {
                                background: #343a40;
                                color: white;
                                padding: 8px;
                                text-align: left;
                                font-weight: bold;
                                border: 1px solid #dee2e6;
                            }
                            .print-table td {
                                padding: 8px;
                                border: 1px solid #dee2e6;
                            }
                            .print-table tr:nth-child(even) {
                                background: #f8f9fa;
                            }
                            .print-table tfoot {
                                background: #e9ecef;
                                font-weight: bold;
                            }
                            .print-totals {
                                margin-top: 20px;
                                padding: 15px;
                                background: #e9ecef;
                                border-radius: 4px;
                                border: 1px solid #dee2e6;
                            }
                            .print-totals-grid {
                                display: grid;
                                grid-template-columns: repeat(4, 1fr);
                                gap: 10px;
                                margin-top: 15px;
                            }
                            .print-total-card {
                                background: white;
                                padding: 10px;
                                border-radius: 4px;
                                border: 1px solid #dee2e6;
                                text-align: center;
                            }
                            .print-total-card h4 {
                                margin: 0 0 5px 0;
                                font-size: 12px;
                                font-weight: bold;
                                color: #495057;
                            }
                            .print-total-card p {
                                margin: 0;
                                font-size: 14px;
                                font-weight: bold;
                                color: #000;
                            }
                            .print-footer {
                                margin-top: 30px;
                                text-align: center;
                                font-size: 10px;
                                color: #666;
                                border-top: 1px solid #ccc;
                                padding-top: 10px;
                            }
                            .no-print {
                                display: none !important;
                            }
                            .status-badge {
                                padding: 2px 8px;
                                border-radius: 12px;
                                font-size: 11px;
                                font-weight: bold;
                            }
                            .status-draft { background: #f8f9fa; color: #6c757d; }
                            .status-ordered { background: #fff3cd; color: #856404; }
                            .status-received { background: #d1edff; color: #0c5460; }
                            .status-cancelled { background: #f8d7da; color: #721c24; }
                        }
                        @media screen {
                            body {
                                font-family: Arial, sans-serif;
                                padding: 20px;
                            }
                        }
                    </style>
                </head>
                <body>
                    ${generatePurchaseOrderPrintContent()}
                    <div class="print-footer">
                        Generated on ${new Date().toLocaleString()} | Daily Skillet
                    </div>
                </body>
                </html>
            `);
    
            printWindow.document.close();
    
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
            }, 500);
    
        } catch (error) {
            console.error('Print error:', error);
            alert('Error generating print report');
        }
    };
    
    const generatePurchaseOrderPrintContent = () => {
        const { purchaseOrders, summary, filter } = reportData;
        
        // Status badge helper
        const getStatusBadge = (status) => {
            const statusClass = {
                'Draft': 'status-draft',
                'Ordered': 'status-ordered',
                'Received': 'status-received',
                'Cancelled': 'status-cancelled',
                'Returned': 'status-cancelled',
                'Partially_Returned': 'status-ordered'
            }[status] || 'status-draft';
            
            return `<span class="status-badge ${statusClass}">${status}</span>`;
        };

        let content = `
            <div class="print-header">
                <h1>PURCHASE ORDER REPORT</h1>
                <div class="subtitle">
                    Period: ${formatDate(filter.startDate)} to ${formatDate(filter.endDate)}
                </div>
                <div class="subtitle">
                    Generated on: ${new Date().toLocaleDateString()}
                </div>
            </div>
        `;
    
        // Summary Cards
        content += `
            <div class="print-totals">
                <h4>REPORT SUMMARY</h4>
                <div class="print-totals-grid">
                    <div class="print-total-card">
                        <h4>Total POs</h4>
                        <p>${summary.totalPOs}</p>
                    </div>
                    <div class="print-total-card">
                        <h4>Total Value</h4>
                        <p>${formatNumber(summary.totalValue)}</p>
                    </div>
                    <div class="print-total-card">
                        <h4>Total Items</h4>
                        <p>${summary.totalItems}</p>
                    </div>
                    <div class="print-total-card">
                        <h4>Received Value</h4>
                        <p>${formatNumber(summary.totalReceivedValue)}</p>
                    </div>
                </div>
            </div>
        `;

        // Status Breakdown
        if (summary.byStatus && Object.keys(summary.byStatus).length > 0) {
            content += `
                <div class="print-summary">
                    <h4>STATUS BREAKDOWN</h4>
                    <table class="print-table">
                        <thead>
                            <tr>
                                <th>Status</th>
                                <th>Count</th>
                                <th>Percentage</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${Object.entries(summary.byStatus).map(([status, count]) => `
                                <tr>
                                    <td>${status}</td>
                                    <td>${count}</td>
                                    <td>${((count / summary.totalPOs) * 100).toFixed(1)}%</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            `;
        }

        // Purchase Orders Table
        content += `
            <div class="print-summary">
                <h4>PURCHASE ORDERS</h4>
                <table class="print-table">
                    <thead>
                        <tr>
                            <th>PO Number</th>
                            <th>Date</th>
                            <th>Supplier</th>
                            <th>Status</th>
                            <th>Items</th>
                            <th>Total Quantity</th>
                            <th>Total Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${purchaseOrders.map(po => `
                            <tr>
                                <td>${po.poNumber}</td>
                                <td>${formatDate(po.orderDate)}</td>
                                <td>${po.supplier?.name || 'N/A'}</td>
                                <td>${getStatusBadge(po.status)}</td>
                                <td>${po.itemSummary.itemsCount}</td>
                                <td>${po.itemSummary.totalQuantity}</td>
                                <td>${formatNumber(po.total)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colspan="5"><strong>TOTAL</strong></td>
                            <td><strong>${summary.totalItems}</strong></td>
                            <td><strong>${formatNumber(summary.totalValue)}</strong></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        `;
    
        return content;
    };

    if (loading && !reportData) {
        return (
            <Layout pageTitle="Loading Purchase Order Report...">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Purchase Order Report">
            <div className="max-w-7xl mx-auto p-6 bg-gray-50">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Purchase Order Report</h1>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => navigate('/reports')}
                            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                        >
                            Back to Reports
                        </button>
                        <button
                            onClick={handlePrintReport}
                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                            disabled={!reportData || reportData.purchaseOrders.length === 0}
                        >
                            Print Report
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {/* Filters Section */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Report Filters</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={dateRange.startDate}
                                onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date
                            </label>
                            <input
                                type="date"
                                value={dateRange.endDate}
                                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded"
                            >
                                <option value="">All Statuses</option>
                                <option value="Draft">Draft</option>
                                <option value="Ordered">Ordered</option>
                                <option value="Received">Received</option>
                                <option value="Cancelled">Cancelled</option>
                                <option value="Returned">Returned</option>
                                <option value="Partially_Returned">Partially Returned</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={handleGenerateReport}
                                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Generate Report'}
                            </button>
                        </div>
                    </div>
                </div>

                {loading && (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
                    </div>
                )}

                {reportData && (
                    <div className="bg-white rounded-lg shadow-md p-6">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-blue-800">Total POs</h3>
                                <p className="text-2xl font-bold">{reportData.summary.totalPOs}</p>
                                <p className="text-sm text-gray-600">Purchase Orders</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-green-800">Total Value</h3>
                                <p className="text-2xl font-bold">{formatNumber(reportData.summary.totalValue)}</p>
                                <p className="text-sm text-gray-600">Overall amount</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-purple-800">Total Items</h3>
                                <p className="text-2xl font-bold">{reportData.summary.totalItems}</p>
                                <p className="text-sm text-gray-600">Items ordered</p>
                            </div>
                            <div className="bg-teal-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-teal-800">Received Value</h3>
                                <p className="text-2xl font-bold">{formatNumber(reportData.summary.totalReceivedValue)}</p>
                                <p className="text-sm text-gray-600">Amount received</p>
                            </div>
                        </div>

                        {/* Status Breakdown */}
                        {reportData.summary.byStatus && Object.keys(reportData.summary.byStatus).length > 0 && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold mb-4">Status Breakdown</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                    {Object.entries(reportData.summary.byStatus).map(([status, count]) => (
                                        <div key={status} className="bg-gray-50 p-3 rounded text-center">
                                            <div className="text-sm text-gray-600 capitalize">{status.replace('_', ' ')}</div>
                                            <div className="text-xl font-bold">{count}</div>
                                            <div className="text-xs text-gray-500">
                                                {((count / reportData.summary.totalPOs) * 100).toFixed(1)}%
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Purchase Orders Table */}
                        <div>
                            <h3 className="text-lg font-semibold mb-4">Purchase Orders</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Quantity</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {reportData.purchaseOrders.map((po) => (
                                            <tr key={po._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap font-medium">{po.poNumber}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">{formatDate(po.orderDate)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">{po.supplier?.name || 'N/A'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                        po.status === 'Received' ? 'bg-green-100 text-green-800' :
                                                        po.status === 'Ordered' ? 'bg-blue-100 text-blue-800' :
                                                        po.status === 'Draft' ? 'bg-gray-100 text-gray-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {po.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">{po.itemSummary.itemsCount}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">{po.itemSummary.totalQuantity}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right font-semibold">
                                                    {formatNumber(po.total)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-100 font-semibold">
                                        <tr>
                                            <td className="px-6 py-4 whitespace-nowrap border-t" colSpan="5">TOTAL</td>
                                            <td className="px-6 py-4 whitespace-nowrap border-t">{reportData.summary.totalItems}</td>
                                            <td className="px-6 py-4 whitespace-nowrap border-t text-right">{formatNumber(reportData.summary.totalValue)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {reportData.purchaseOrders.length === 0 && !loading && (
                            <div className="text-center py-8">
                                <p className="text-gray-500">No purchase orders found for the selected filters</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default PurchaseOrderReportPage;